import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { QueryState } from '@/components/QueryState'
import { listUsers } from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import { CustomerFormDialog } from '@/features/customers/CustomerFormDialog'
import { deleteCustomer, getCustomer, reassignCustomer } from '@/features/customers/api'
import { formatMoney } from '@/features/inventory/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const INPUT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  )
}

/**
 * Hand the account to another rep, or take it off anyone's book.
 *
 * `assigned_rep_id` is a user id, and the only endpoint that lists users needs
 * `users:read` -- a grant `sales_manager` does not hold despite carrying `customers:delete`
 * (the permission this whole control is gated on) and being the primary audience for it.
 * Where the picker is available this renders the real names; where it is not, it falls back
 * to a plain id field so the control still works, just without the lookup.
 */
function ReassignPanel({ customerId, currentRepId }: { customerId: string; currentRepId: string | null }) {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const mayListUsers = can('users', 'read')
  const [manualId, setManualId] = useState('')

  const users = useQuery({
    queryKey: ['users', 1],
    queryFn: () => listUsers(1),
    retry: false,
    enabled: mayListUsers,
  })

  const reassign = useMutation({
    mutationFn: (repId: string | null) => reassignCustomer(customerId, repId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer', customerId] })
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Assignment updated.')
      setManualId('')
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  return (
    <Card>
      <CardHeader
        title="Assignment"
        description="Handing this account to another rep takes it off the current one's book."
      />
      <CardBody className="space-y-3">
        {mayListUsers ? (
          <div className="flex items-center gap-2">
            <select
              className={SELECT_CLASS}
              value={currentRepId ?? ''}
              disabled={users.isLoading || reassign.isPending}
              onChange={(event) => reassign.mutate(event.target.value || null)}
            >
              <option value="">Unassigned</option>
              {(users.data?.data ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              className={INPUT_CLASS}
              placeholder="Rep's user ID"
              value={manualId}
              onChange={(event) => setManualId(event.target.value)}
            />
            <Button
              variant="secondary"
              loading={reassign.isPending}
              disabled={!manualId.trim()}
              onClick={() => reassign.mutate(manualId.trim())}
            >
              Assign
            </Button>
            {currentRepId && (
              <Button variant="ghost" onClick={() => reassign.mutate(null)} disabled={reassign.isPending}>
                Unassign
              </Button>
            )}
          </div>
        )}
        {!mayListUsers && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {currentRepId ? `Currently assigned to user ${currentRepId}.` : 'Currently unassigned.'} Paste
            the rep's user ID above — this role cannot browse the user list.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * One customer: who they are, what they owe, and who carries the account.
 */
export function CustomerDetail() {
  const { customerId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const can = useAuth((s) => s.can)

  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => getCustomer(customerId),
    retry: false,
  })

  const remove = useMutation({
    mutationFn: () => deleteCustomer(customerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Customer removed.')
      navigate('/customers')
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(false)
    },
  })

  return (
    <div className="space-y-4">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        All customers
      </Link>

      <QueryState isLoading={customer.isLoading} error={customer.error}>
        {customer.data && (
          <>
            <Card>
              <CardHeader
                title={customer.data.name}
                description={customer.data.code}
                action={
                  <div className="flex items-center gap-2">
                    {can('customers', 'update') && (
                      <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                        Edit
                      </Button>
                    )}
                    {can('customers', 'delete') && customer.data.status !== 'inactive' && (
                      <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
                        Remove
                      </Button>
                    )}
                  </div>
                }
              />
              <CardBody>
                <dl className="grid grid-cols-2 gap-4 tablet:grid-cols-4 desktop:grid-cols-6">
                  <Fact label="Tier" value={customer.data.tier} />
                  <Fact label="Status" value={customer.data.status} />
                  <Fact label="Email" value={customer.data.email ?? '—'} />
                  <Fact label="Phone" value={customer.data.phone ?? '—'} />
                  <Fact
                    label="City"
                    value={[customer.data.city, customer.data.state].filter(Boolean).join(', ') || '—'}
                  />
                  <Fact
                    label="Credit limit"
                    value={customer.data.credit_limit ? formatMoney(customer.data.credit_limit) : '—'}
                  />
                  <Fact label="Payment terms" value={`${customer.data.payment_terms_days} days`} />
                  <Fact label="Tax ID" value={customer.data.tax_id ?? '—'} />
                </dl>
                {customer.data.notes && (
                  <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{customer.data.notes}</p>
                )}
              </CardBody>
            </Card>

            {can('customers', 'delete') && (
              <ReassignPanel customerId={customer.data.id} currentRepId={customer.data.assigned_rep_id} />
            )}

            {editing && (
              <CustomerFormDialog open customer={customer.data} onClose={() => setEditing(false)} />
            )}

            <Modal
              open={confirmDelete}
              onClose={() => setConfirmDelete(false)}
              title={`Remove ${customer.data.code}?`}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                    Remove
                  </Button>
                </>
              }
            >
              <Alert tone="info">
                This is a soft delete: the account is marked inactive and hidden from new orders, but
                every past order and invoice against it stays exactly as it was.
              </Alert>
            </Modal>
          </>
        )}
      </QueryState>
    </div>
  )
}
